import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Form, Modal, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Add, TickCircle } from 'iconsax-react';
import Select from 'react-select';
import MyApiClient from '../../../api-client/MyApiClient';
import { aggiungiFotoGalleria } from '../../../api-client/appPlayer';
import { useProfile } from '../../../hooks/useProfile';
import { useFederations } from '../../../hooks/useFederations';
import { italianProvinces, sports } from '../../components/Util/enum';
import CampoRicerca from '../../components/Util/CampoRicerca';
import { segnalaMancanti } from '../../components/Util/campiMancanti';
import chiediConferma from '../../components/Util/chiediConferma';
import ProductImageUploader from '../../components/Util/ProductImageUploader';
import { sidSelectStyles } from '../../components/Util/sidSelectStyles';

/*
 * Percorso guidato per attivare la struttura (il tasto «Completa il profilo» del banner «Il profilo non è attivo»).
 * Due passi, con solo i dati obbligatori:
 *   1. la struttura, dove si trova, il referente legale;
 *   2. gli sport, il logo e almeno una foto.
 * Ogni passo si salva da solo: si puo` interrompere e riprendere. I dati sono gli stessi di La mia struttura.
 */

const TIPOLOGIE = [
    { value: 'ASD', label: 'ASD' },
    { value: 'SSD', label: 'SSD' },
    { value: 'INDIVIDUAL_COMPANY', label: 'Ditta individuale' },
    { value: 'CAPITAL_COMPANY', label: 'SRL / SPA' },
];
const NATURE = [
    { value: 'SCHOOL', label: 'Scuola' },
    { value: 'GYM', label: 'Palestra' },
    { value: 'RENTAL_SHOP', label: 'Rental Shop' },
    { value: 'CIRCLE', label: 'Club' },
    { value: 'SPORTS_CLUB', label: 'Società Sportiva' },
];

const VUOTO = {
    name: '', phoneNumber: '', incorporationYear: '', clubNature: '', clubSubNature: '',
    streetAddress: '', locality: '', province: '', postalCode: '',
    legalUguale: true, legalStreetAddress: '', legalLocality: '', legalProvince: '', legalPostalCode: '',
    repFirstName: '', repLastName: '', repBirthPlace: '', repCity: '', repAddress: '', repTaxCode: '',
};

const OBBLIGATORI_1 = [
    ['name', 'Nome della struttura'], ['phoneNumber', 'Telefono'], ['incorporationYear', 'Anno di costituzione'],
    ['clubNature', 'Tipologia giuridica'], ['clubSubNature', 'Natura della struttura'],
    ['streetAddress', 'Via'], ['locality', 'Comune'], ['province', 'Provincia'], ['postalCode', 'CAP'],
    ['repFirstName', 'Nome'], ['repLastName', 'Cognome'], ['repBirthPlace', 'Luogo di nascita'],
    ['repCity', 'Città di residenza'], ['repAddress', 'Indirizzo di residenza'], ['repTaxCode', 'Codice fiscale'],
];
const OBBLIGATORI_SEDE_LEGALE = [
    ['legalStreetAddress', 'Via della sede legale'], ['legalLocality', 'Comune della sede legale'],
    ['legalProvince', 'Provincia della sede legale'], ['legalPostalCode', 'CAP della sede legale'],
];

const token = () => { try { return JSON.parse(sessionStorage.getItem('userDetails')).idToken; } catch { return ''; } };

export const Onboarding = () => {
    const navigate = useNavigate();
    const { profile, fetchProfile } = useProfile();
    const { federations } = useFederations();

    const [passo, setPasso] = useState(1);
    const [f, setF] = useState(VUOTO);
    const [inCorso, setInCorso] = useState(false);
    const [errore, setErrore] = useState('');
    const iniziale = useRef(false);

    // Step 2
    const [sport, setSport] = useState('');
    const [organismi, setOrganismi] = useState([]);
    const [uploader, setUploader] = useState(null); // 'logo' | 'foto'

    const club = profile?.club;

    // Precompila con quello che c'e` gia` nel profilo (una volta sola)
    useEffect(() => {
        if (!club || iniziale.current) return;
        iniziale.current = true;
        const uguale = !club.legalAddress?.streetAddress || (club.legalAddress.streetAddress === club.address?.streetAddress);
        setF({
            ...VUOTO,
            name: club.name || '', phoneNumber: club.phoneNumber || '', incorporationYear: club.incorporationYear || '',
            clubNature: club.clubNature || '', clubSubNature: club.clubSubNature || '',
            streetAddress: club.address?.streetAddress || '', locality: club.address?.locality || '',
            province: club.address?.province || '', postalCode: club.address?.postalCode || '',
            legalUguale: uguale,
            legalStreetAddress: club.legalAddress?.streetAddress || '', legalLocality: club.legalAddress?.locality || '',
            legalProvince: club.legalAddress?.province || '', legalPostalCode: club.legalAddress?.postalCode || '',
            repFirstName: club.legalRepresentativeFirstName || '', repLastName: club.legalRepresentativeLastName || '',
            repBirthPlace: club.legalRepresentativeBirthPlace || '', repCity: club.legalRepresentativeCity || '',
            repAddress: club.legalRepresentativeAddress || '', repTaxCode: club.legalRepresentativeTaxCode || '',
        });
    }, [club]);

    const imposta = (campo) => (e) => {
        const valore = e?.target ? e.target.value : e?.value ?? '';
        setF((prima) => ({ ...prima, [campo]: valore }));
    };

    const Campo = ({ campo, etichetta, tipo = 'text', maiuscolo = false, max }) => (
        <div className="mb-3">
            <Form.Label htmlFor={`ob-${campo}`}>{etichetta} *</Form.Label>
            <Form.Control
                id={`ob-${campo}`}
                type={tipo}
                value={f[campo]}
                maxLength={max}
                onChange={(e) => imposta(campo)({ target: { value: maiuscolo ? e.target.value.toUpperCase() : e.target.value } })}
            />
        </div>
    );
    const Tendina = ({ campo, etichetta, opzioni }) => (
        <div className="mb-3">
            <Form.Label htmlFor={`ob-${campo}`}>{etichetta} *</Form.Label>
            <Select
                inputId={`ob-${campo}`}
                styles={sidSelectStyles}
                options={opzioni}
                value={opzioni.find((o) => o.value === f[campo]) || null}
                onChange={imposta(campo)}
                noOptionsMessage={() => 'Nessun risultato'}
            />
        </div>
    );

    /* ───────── Passo 1 ───────── */

    const avanti = async () => {
        // Dati mancanti: niente finestra, campi evidenziati con il messaggio sotto e cursore sul primo (regola SUI)
        const elenco = [...OBBLIGATORI_1, ...(f.legalUguale ? [] : OBBLIGATORI_SEDE_LEGALE)]
            .filter(([campo]) => !String(f[campo] ?? '').trim())
            .map(([campo, etichetta]) => ({ campo: `ob-${campo}`, messaggio: `Manca: ${etichetta}` }));
        if (segnalaMancanti(elenco) > 0) return;
        setInCorso(true);
        try {
            const p = JSON.parse(JSON.stringify(profile));
            const c = p.club;
            c.name = f.name.trim();
            c.phoneNumber = f.phoneNumber.trim();
            c.incorporationYear = f.incorporationYear;
            c.clubNature = f.clubNature;
            c.clubSubNature = f.clubSubNature;
            c.spokenLocales = c.spokenLocales?.length ? c.spokenLocales : ['it'];
            c.legalRepresentativeFirstName = f.repFirstName.trim();
            c.legalRepresentativeLastName = f.repLastName.trim();
            c.legalRepresentativeBirthPlace = f.repBirthPlace.trim();
            c.legalRepresentativeCity = f.repCity.trim();
            c.legalRepresentativeAddress = f.repAddress.trim();
            c.legalRepresentativeTaxCode = f.repTaxCode.trim().toUpperCase();
            c.address = { streetAddress: f.streetAddress.trim(), locality: f.locality.trim(), country: 'Italia', postalCode: f.postalCode.trim(), province: f.province };
            c.legalAddress = f.legalUguale
                ? { ...c.address }
                : { streetAddress: f.legalStreetAddress.trim(), locality: f.legalLocality.trim(), country: 'Italia', postalCode: f.legalPostalCode.trim(), province: f.legalProvince };
            delete c.logo;
            delete c.pictures;
            const r = await new MyApiClient().updateProfile(token(), p);
            if (r?.error) { setErrore(`Si è verificato un errore durante il salvataggio (${r.error})`); return; }
            // Il menu «Organismo sociale» dipende dalla tipologia: si aggiorna senza rifare il login
            try {
                const td = JSON.parse(sessionStorage.getItem('userDetails'));
                td.clubNature = f.clubNature;
                sessionStorage.setItem('userDetails', JSON.stringify(td));
            } catch { /* niente */ }
            await fetchProfile(true);
            setPasso(2);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (e) {
            setErrore('Non è stato possibile salvare. Riprova.');
        } finally {
            setInCorso(false);
        }
    };

    /* ───────── Passo 2 ───────── */

    const sportAggiunti = club?.sports || [];
    const haLogo = !!club?.logo;
    const nFoto = (club?.pictures || []).length;
    const conOrganismi = club?.clubNature === 'ASD' || club?.clubNature === 'SSD';

    const organismiDisponibili = useMemo(() => {
        if (!sport || !federations?.length) return [];
        const s = sport.toUpperCase();
        return federations
            .filter((fed) => !fed.sports?.length || fed.sports.includes(s))
            .map((fed) => ({ value: fed.code, label: `${fed.code} - ${fed.description}` }));
    }, [sport, federations]);

    const salva = async (modifica, ok) => {
        setInCorso(true);
        try {
            const p = JSON.parse(JSON.stringify(profile));
            modifica(p.club);
            const r = await new MyApiClient().updateProfile(token(), p);
            if (r?.error) { setErrore(`Si è verificato un errore durante il salvataggio (${r.error})`); return false; }
            await fetchProfile(true);
            ok?.();
            return true;
        } catch (e) {
            setErrore('Non è stato possibile salvare. Riprova.');
            return false;
        } finally {
            setInCorso(false);
        }
    };

    const aggiungiSport = async () => {
        if (!sport) return;
        if (sportAggiunti.some((s) => s.sportType === sport)) { setErrore('Questo sport c\'è già.'); return; }
        if (conOrganismi && organismi.length === 0) { setErrore('Per le ASD e le SSD serve almeno un organismo sportivo.'); return; }
        await salva((c) => {
            const attuali = (c.sports || []).map((s) => ({ sportType: s.sportType, ...(s.sportPromotionBody?.length ? { sportPromotionBody: s.sportPromotionBody } : {}) }));
            c.sports = [...attuali, { sportType: sport, ...(organismi.length ? { sportPromotionBody: organismi.map((o) => o.value) } : {}) }];
            delete c.logo;
            delete c.pictures;
            delete c.invoiceDataSender;
        }, () => { setSport(''); setOrganismi([]); });
    };

    const rimuoviSport = async (s, nome) => {
        if (!(await chiediConferma({ titolo: `Rimuovere ${nome}?`, testo: 'Lo sport sparisce dalla struttura.', conferma: 'Rimuovi' }))) return;
        await salva((c) => {
            c.sports = (c.sports || [])
                .filter((x) => x.sportType !== s.sportType)
                .map((x) => ({ sportType: x.sportType, ...(x.sportPromotionBody?.length ? { sportPromotionBody: x.sportPromotionBody } : {}) }));
            delete c.logo;
            delete c.pictures;
            delete c.invoiceDataSender;
        });
    };

    const salvaImmagine = async (base64) => {
        const cosa = uploader;
        setUploader(null);
        if (cosa === 'logo') {
            await salva((c) => { c.logo = base64; delete c.pictures; });
        } else {
            setInCorso(true);
            const r = await aggiungiFotoGalleria(base64, 'foto-struttura.jpg', '');
            setInCorso(false);
            if (r?.error) { setErrore(r.error); return; }
            await fetchProfile(true);
        }
    };

    // Caricare la foto non ricalcola lo stato del profilo nel backend (solo un salvataggio del profilo lo fa): un salvataggio
    // senza modifiche, alla fine, lo porta da «non attivo» a completo. Poi si ricarica la dashboard con lo stato nuovo.
    const fine = async () => {
        // Dati mancanti (regola SUI): niente tasto spento, si evidenzia cosa manca
        const mancanti = [];
        if (sportAggiunti.length === 0) mancanti.push({ campo: 'campo-sport-ob', messaggio: 'Manca almeno uno sport: sceglilo e premi «Aggiungi sport»' });
        if (!haLogo) mancanti.push({ campo: 'ob-logo', messaggio: 'Manca il logo della struttura' });
        if (nFoto === 0) mancanti.push({ campo: 'ob-foto', messaggio: 'Manca almeno una foto della struttura' });
        if (segnalaMancanti(mancanti) > 0) return;
        const ok = await salva((c) => { delete c.logo; delete c.pictures; delete c.invoiceDataSender; });
        if (ok) window.location.assign('/struttura/dashboard');
    };

    if (!club) return <div className="text-center py-5"><Spinner animation="border" role="status" /></div>;

    const Requisito = ({ fatto, testo }) => (
        <span className={`sid-onboarding__requisito${fatto ? ' is-fatto' : ''}`}>
            <TickCircle size={16} variant={fatto ? 'Bold' : 'Linear'} /> {testo}
        </span>
    );

    return (
        <>
            <Modal show={!!errore} onHide={() => setErrore('')} centered>
                <Modal.Header closeButton><Modal.Title>Controlla i dati</Modal.Title></Modal.Header>
                <Modal.Body>{errore}</Modal.Body>
                <Modal.Footer className="justify-content-center"><Button className="btn btn-primary btn-add" onClick={() => setErrore('')}>Chiudi</Button></Modal.Footer>
            </Modal>
            <Modal show={inCorso} centered><Modal.Body className="text-center"><Spinner animation="border" role="status" /><div>Salvataggio in corso...</div></Modal.Body></Modal>
            <ProductImageUploader
                show={!!uploader}
                onClose={() => setUploader(null)}
                onSave={salvaImmagine}
                titolo={uploader === 'logo' ? 'Logo della struttura' : 'Foto della struttura'}
                larghezza={uploader === 'logo' ? 400 : 1280}
                altezza={uploader === 'logo' ? 400 : 720}
                formato={uploader === 'logo' ? 'png' : 'jpeg'}
            />

            <div className="sid-onboarding">
                <div className="sid-onboarding__avanzamento" role="progressbar" aria-valuemin={1} aria-valuemax={2} aria-valuenow={passo} aria-label={`Passo ${passo} di 2`}>
                    <strong>Passo {passo} di 2</strong>
                    <div className="sid-onboarding__barra"><span style={{ width: `${passo * 50}%` }} /></div>
                </div>

                {passo === 1 && (
                    <>
                        <Card>
                            <Card.Body className="sid-card-body--spaced">
                                <h5 className="sid-form-section">La struttura</h5>
                                {Campo({ campo: 'name', etichetta: 'Nome della struttura', max: 120 })}
                                {Campo({ campo: 'phoneNumber', etichetta: 'Telefono', tipo: 'tel', max: 20 })}
                                {Campo({ campo: 'incorporationYear', etichetta: 'Anno di costituzione', tipo: 'number', max: 4 })}
                                {Tendina({ campo: 'clubNature', etichetta: 'Tipologia giuridica', opzioni: TIPOLOGIE })}
                                {Tendina({ campo: 'clubSubNature', etichetta: 'Natura della struttura', opzioni: NATURE })}
                            </Card.Body>
                        </Card>
                        <Card className="mt-4">
                            <Card.Body className="sid-card-body--spaced">
                                <h5 className="sid-form-section">Dove si trova</h5>
                                {Campo({ campo: 'streetAddress', etichetta: 'Via e numero', max: 120 })}
                                <div className="sid-onboarding__terna">
                                    {Campo({ campo: 'locality', etichetta: 'Comune', max: 80 })}
                                    {Tendina({ campo: 'province', etichetta: 'Provincia', opzioni: italianProvinces })}
                                    {Campo({ campo: 'postalCode', etichetta: 'CAP', max: 5 })}
                                </div>
                                <Form.Check
                                    type="switch"
                                    id="ob-legale-uguale"
                                    label="La sede legale è la stessa"
                                    checked={f.legalUguale}
                                    onChange={(e) => setF((prima) => ({ ...prima, legalUguale: e.target.checked }))}
                                />
                                {!f.legalUguale && (
                                    <div className="mt-3">
                                        {Campo({ campo: 'legalStreetAddress', etichetta: 'Via e numero della sede legale', max: 120 })}
                                        <div className="sid-onboarding__terna">
                                            {Campo({ campo: 'legalLocality', etichetta: 'Comune', max: 80 })}
                                            {Tendina({ campo: 'legalProvince', etichetta: 'Provincia', opzioni: italianProvinces })}
                                            {Campo({ campo: 'legalPostalCode', etichetta: 'CAP', max: 5 })}
                                        </div>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                        <Card className="mt-4">
                            <Card.Body className="sid-card-body--spaced">
                                <h5 className="sid-form-section">Il referente legale</h5>
                                <div className="sid-onboarding__coppia">
                                    {Campo({ campo: 'repFirstName', etichetta: 'Nome', max: 60 })}
                                    {Campo({ campo: 'repLastName', etichetta: 'Cognome', max: 60 })}
                                </div>
                                {Campo({ campo: 'repTaxCode', etichetta: 'Codice fiscale', maiuscolo: true, max: 16 })}
                                {Campo({ campo: 'repBirthPlace', etichetta: 'Luogo di nascita', max: 80 })}
                                {Campo({ campo: 'repCity', etichetta: 'Città di residenza', max: 80 })}
                                {Campo({ campo: 'repAddress', etichetta: 'Indirizzo di residenza', max: 120 })}
                            </Card.Body>
                        </Card>
                        <div className="d-flex justify-content-end gap-4 mt-4">
                            <Button className="btn btn-light btn-add" onClick={() => navigate('/struttura/dashboard')}>Più tardi</Button>
                            <Button className="btn btn-primary btn-add" onClick={avanti} disabled={inCorso}>Avanti</Button>
                        </div>
                    </>
                )}

                {passo === 2 && (
                    <>
                        <Card>
                            <Card.Body className="sid-card-body--spaced">
                                <h5 className="sid-form-section">Gli sport</h5>
                                {sportAggiunti.length > 0 && (
                                    <div className="sid-onboarding__chips">
                                        {sportAggiunti.map((s) => {
                                            const nome = sports.find((x) => x.value === s.sportType)?.label || s.sportType;
                                            return (
                                                <span key={s.sportType} className="sid-onboarding__sport">
                                                    <span>{nome}{s.sportPromotionBody?.length ? ` · ${s.sportPromotionBody.join(', ')}` : ''}</span>
                                                    <button type="button" aria-label={`Rimuovi ${nome}`} title={`Rimuovi ${nome}`} onClick={() => rimuoviSport(s, nome)} disabled={inCorso}>×</button>
                                                </span>
                                            );
                                        })}
                                    </div>
                                )}
                                <Form.Label htmlFor="campo-sport-ob">Aggiungi uno sport *</Form.Label>
                                <CampoRicerca id="campo-sport-ob" opzioni={sports} valore={sport} onChange={(v) => { setSport(v); setOrganismi([]); }} minCaratteri={2} massimo={10} />
                                {sport && conOrganismi && (
                                    <div className="mt-3">
                                        <Form.Label>Organismi sportivi *</Form.Label>
                                        <Select
                                            isMulti
                                            styles={sidSelectStyles}
                                            options={organismiDisponibili}
                                            value={organismi}
                                            onChange={(v) => setOrganismi(v || [])}
                                            noOptionsMessage={() => 'Nessun risultato'}
                                        />
                                    </div>
                                )}
                                {sport && (
                                    <div className="mt-3">
                                        <Button className="btn btn-primary sid-btn-compatto" onClick={aggiungiSport} disabled={inCorso}>
                                            <Add size={14} /><span>Aggiungi sport</span>
                                        </Button>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                        <Card className="mt-4">
                            <Card.Body className="sid-card-body--spaced">
                                <h5 className="sid-form-section">Logo e foto</h5>
                                <div className="mb-4">
                                    <Form.Label>Logo *</Form.Label>
                                    <div className="sid-galleria__griglia">
                                        <div className="sid-galleria__foto">
                                            <div className="sid-galleria__immagine sid-galleria__immagine--logo">
                                                {haLogo ? <img src={club.logo} alt="Logo" /> : null}
                                                <span className="sid-galleria__posizione">Logo</span>
                                            </div>
                                            <Button id="ob-logo" className="btn btn-light sid-btn-compatto mt-2" onClick={() => setUploader('logo')}>{haLogo ? 'Cambia il logo' : 'Carica il logo'}</Button>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <Form.Label>Foto *</Form.Label>
                                    <div className="sid-galleria__griglia">
                                        {(club.pictures || []).map((p) => (
                                            <div className="sid-galleria__foto" key={p.id}>
                                                <div className="sid-galleria__immagine"><img src={p.uri} alt="" /></div>
                                            </div>
                                        ))}
                                        <button type="button" id="ob-foto" className="sid-galleria__aggiungi" aria-label="Aggiungi una foto" title="Aggiungi una foto" onClick={() => setUploader('foto')}>
                                            <Add size={28} />
                                        </button>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                        <div className="sid-onboarding__requisiti mt-4">
                            <Requisito fatto={sportAggiunti.length > 0} testo="Almeno uno sport" />
                            {Requisito({ fatto: haLogo, testo: 'Il logo' })}
                            <Requisito fatto={nFoto > 0} testo="Almeno una foto" />
                        </div>
                        <div className="d-flex justify-content-end gap-4 mt-4">
                            <Button className="btn btn-light btn-add" onClick={() => setPasso(1)}>Indietro</Button>
                            <Button className="btn btn-primary btn-add" onClick={fine} disabled={inCorso}>Fine</Button>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default Onboarding;
